import { useState } from "react";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import { farmCoverAlt, farmPhotoAlt } from "@/lib/imageAlt";
import type { Farm } from "@/mocks/farms";

export default function FarmDetailGallery({ farm }: { farm: Farm }) {
  const photos = [
    { src: farm.image, alt: farmCoverAlt(farm) },
    ...farm.photos.map((src, index) => ({
      src,
      alt: farmPhotoAlt(farm, index, farm.photos.length),
    })),
  ];
  const [activeIndex, setActiveIndex] = useState(0);
  const active = photos[activeIndex] ?? photos[0];

  return (
    <section className="sun-glaze bg-background-100 py-20 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-12">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              A Look Around
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
            Inside {farm.name}
          </h2>
        </Reveal>

        <Reveal>
          <div className="rounded-lg overflow-hidden w-full h-[22rem] md:h-[32rem] border border-background-200">
            <img
              src={active.src}
              width={1200}
              height={900}
              alt={active.alt}
              title={`${farm.name} - ${farm.crop} farm in ${farm.village}`}
              className="w-full h-full object-cover object-top transition-opacity duration-300"
              decoding="async"
            />
          </div>

          <div className="grid grid-cols-4 gap-2 md:gap-3 mt-3 md:mt-4">
            {photos.map((photo, index) => (
              <button
                key={`${farm.slug}-gallery-${index}`}
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-label={`Show photo ${index + 1} of ${photos.length}: ${photo.alt}`}
                className={`h-20 md:h-24 rounded-md overflow-hidden border-2 transition-colors cursor-pointer ${
                  activeIndex === index ? "border-accent-500" : "border-transparent hover:border-accent-300"
                }`}
              >
                <img
                  src={photo.src}
                  width={400}
                  height={300}
                  alt={photo.alt}
                  className="w-full h-full object-cover object-top"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}